/**
 * Die Rollenstufen — als Spiegel, nicht als Tor.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIESE DATEI SCHÜTZT NICHTS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Die Prüfung steht in der Datenbank: in den Schreibpolicies (Migration `0070`)
 * und in den zehn Definer-Toren (`0072`). Wer hier etwas ändert, ändert nur,
 * was die Oberfläche ANBIETET — nicht, was möglich ist. Ein Aufruf über
 * `supabase.rpc()` aus der Entwicklerkonsole wird trotzdem abgewiesen, und das
 * ist der ganze Sinn der Aufteilung.
 *
 * Wozu es die Datei dann gibt: damit niemand vor einem Knopf sitzt, der sicher
 * scheitert. Ein Knopf, der quittiert und nichts tut, ist schlimmer als ein
 * Knopf, den es nicht gibt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIE VIER STUFEN, UND WAS SIE BEDEUTEN
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   owner    — der Vermieter. Darf alles.
 *   manager  — gleichwertig zum Inhaber. Bewusst ohne eigene Bedeutung: bei bis
 *              zu 15 Einheiten wäre eine Zwischenstufe eine Dublette. Der Wert
 *              bleibt im Enum, weil Postgres Enum-Werte nicht sauber entfernen
 *              kann und die Policies aus `0001` ihn ohnehin führen.
 *   staff    — der Alltag: Buchungen, Gäste, Meldescheine, Kalender, Angebote.
 *              KEIN Geld, KEINE Löschung, KEINE Behördenmeldung. Gedacht für
 *              eine Reinigungskraft oder eine Aushilfe an der Rezeption.
 *   viewer   — darf lesen. Sonst nichts, auf keiner Tabelle.
 *
 * Die Zuordnung unten muss mit `current_tenant_ids(member_role[])` in den
 * Policies übereinstimmen. Laufen sie auseinander, gewinnt die Datenbank — und
 * der Vermieter sieht einen Knopf, der nichts tut. Deshalb steht neben jeder
 * Handlung, welche Tabelle oder Funktion sie betrifft.
 */

export type Stufe = 'owner' | 'manager' | 'staff' | 'viewer'

/** Was die Oberfläche unterscheiden muss. Nicht jede Tabelle, nur jede Absicht. */
export type Handlung =
  /** Buchungen, Gäste, Meldescheine, Kalender, Angebote — Gruppe ALLTAG in `0070`. */
  | 'alltag'
  /** Rechnung ausstellen oder stornieren — `issue_invoice`, `cancel_invoice`. */
  | 'rechnung'
  /** Ortstaxe einreichen — `submit_tourist_tax_filing`. */
  | 'meldung'
  /** Gast löschen, anonymisieren, zusammenführen, exportieren. */
  | 'gastdaten'
  /** Preise, Einheiten, Objekte, Gebühren, Vorlagen, Kanäle — Gruppe BETRIEB. */
  | 'stammdaten'
  /** Abonnement, Zahlungsmittel, KI-Kosten. */
  | 'abrechnung'

const MATRIX: Record<Handlung, readonly Stufe[]> = {
  alltag:     ['owner', 'manager', 'staff'],
  rechnung:   ['owner', 'manager'],
  meldung:    ['owner', 'manager'],
  gastdaten:  ['owner', 'manager'],
  stammdaten: ['owner', 'manager'],
  abrechnung: ['owner'],
}

/**
 * Darf diese Stufe das?
 *
 * `null` heißt „Stufe unbekannt" und antwortet **ja**. Das ist Absicht: In der
 * Demo gibt es keine Mitgliedschaft, und während des Ladens ist die Stufe noch
 * nicht da. Ein `false` wäre dort falsch — es würde Knöpfe ausblenden, die dem
 * Vermieter zustehen, und zwar ausgerechnet in der Sekunde, in der die App
 * startet. Die Datenbank weist ohnehin ab, wenn es doch nicht stimmt.
 */
export function darf(stufe: Stufe | null, was: Handlung): boolean {
  if (stufe === null) return true
  return MATRIX[was].includes(stufe)
}

/** Wer es darf — für den Satz, den ein Abgewiesener zu lesen bekommt. */
export function werDarf(was: Handlung): string {
  const stufen = MATRIX[was]
  if (stufen.length === 1) return 'Das darf nur der Inhaber.'
  if (stufen.includes('staff')) return 'Das darf der Inhaber, ein Verwalter oder ein Mitarbeiter.'
  return 'Das darf nur der Inhaber oder ein Verwalter.'
}

/** Lesbarer Name der Stufe, für Einstellungen und Fehlermeldungen. */
export function stufenName(stufe: Stufe): string {
  switch (stufe) {
    case 'owner': return 'Inhaber'
    case 'manager': return 'Verwalter'
    case 'staff': return 'Mitarbeiter'
    case 'viewer': return 'Nur lesend'
  }
}
